import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Clock, ShieldCheck, CheckCircle2, Users, Upload, CalendarClock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api, { fmtEuro, fmtDate } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => setError("Errore nel caricamento delle statistiche"));
  }, []);

  const cards = stats
    ? [
        { label: "F24 Totali", value: stats.totals.totale, sub: fmtEuro(stats.totals.importo_totale), icon: FileText, color: "text-navy", testid: "stat-f24-totali" },
        { label: "In Attesa di Conferma", value: stats.totals.in_attesa, sub: fmtEuro(stats.totals.importo_in_attesa), icon: Clock, color: "text-amber-600", testid: "stat-f24-attesa" },
        { label: "Autorizzati", value: stats.totals.autorizzato, sub: fmtEuro(stats.totals.importo_autorizzato), icon: ShieldCheck, color: "text-blue-600", testid: "stat-f24-autorizzati" },
        { label: "Pagati", value: stats.totals.pagato, sub: fmtEuro(stats.totals.importo_pagato), icon: CheckCircle2, color: "text-emerald-600", testid: "stat-f24-pagati" },
      ]
    : [];

  return (
    <AdminLayout title="Dashboard" subtitle="Panoramica dei Modelli F24 dello studio">
      {error && <div data-testid="dashboard-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-6">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="admin-dashboard">
        {cards.map((c, i) => (
          <div
            key={c.label}
            data-testid={c.testid}
            className="bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 animate-fade-up"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{c.label}</p>
                <p className="font-display text-4xl font-bold text-navy mt-2">{c.value}</p>
                <p className={`text-sm font-medium mt-1 ${c.color}`}>{c.sub}</p>
              </div>
              <c.icon className={`w-6 h-6 ${c.color} opacity-60`} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-white rounded-xl border border-slate-200/80 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-xl font-bold text-navy">Importi per anno</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Tributi pagati, autorizzati e in attesa</p>
            </div>
            <Link to="/admin/f24">
              <Button data-testid="goto-f24-button" className="bg-navy hover:bg-navy-light text-white" size="sm">
                <Upload className="w-4 h-4 mr-2" />
                Carica F24
              </Button>
            </Link>
          </div>
          {stats && stats.by_year.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.by_year} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => `€${(v / 1000).toFixed(1)}k`} />
                <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="importo_pagato" name="Pagato" stackId="a" fill="#1E4020" radius={[0, 0, 0, 0]} />
                <Bar dataKey="importo_autorizzato" name="Autorizzato" stackId="a" fill="#3C763D" />
                <Bar dataKey="importo_in_attesa" name="In attesa" stackId="a" fill="#F59E0B" />
                <Bar dataKey="importo_scaduto" name="Scaduto non pagato" stackId="a" fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-16 text-center">Nessun F24 caricato. Carica i primi file per vedere le statistiche.</p>
          )}
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-navy">Prossime scadenze</h2>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarClock className="w-3.5 h-3.5" />
                In attesa di conferma
              </span>
            </div>
            {stats && stats.upcoming.length > 0 ? (
              <ul className="divide-y divide-slate-100" data-testid="upcoming-list">
                {stats.upcoming.map((f) => (
                  <li key={f.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-navy truncate">{f.client_name}</p>
                      <p className="text-xs text-muted-foreground">Scadenza {fmtDate(f.due_date)}</p>
                    </div>
                    <p className="text-sm font-bold text-gold whitespace-nowrap">{f.amount != null ? fmtEuro(f.amount) : "—"}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">Nessuna scadenza imminente.</p>
            )}
          </div>

          <div className="bg-navy rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-gold" />
              <h2 className="font-display text-xl font-bold">Clienti attivi</h2>
            </div>
            <p className="font-display text-4xl font-bold text-gold-bright" data-testid="stat-clienti-count">{stats?.clients_count ?? "—"}</p>
            <Link to="/admin/clienti" className="inline-block mt-4 text-sm text-white/70 hover:text-gold-bright transition-colors duration-200" data-testid="goto-clienti-link">
              Gestisci anagrafiche →
            </Link>
          </div>
        </div>
      </div>

      {stats && stats.recent.length > 0 && (
        <div className="mt-6 bg-white rounded-xl border border-slate-200/80 shadow-sm p-6">
          <h2 className="font-display text-xl font-bold text-navy mb-4">Ultimi F24 caricati</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-f24-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-slate-100">
                  <th className="pb-3 font-semibold">Cliente</th>
                  <th className="pb-3 font-semibold">File</th>
                  <th className="pb-3 font-semibold">Scadenza</th>
                  <th className="pb-3 font-semibold text-right">Importo</th>
                  <th className="pb-3 font-semibold text-right">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {stats.recent.map((f) => (
                  <tr key={f.id} className="hover:bg-slate-50/60 transition-colors duration-150">
                    <td className="py-3 font-medium text-navy">{f.client_name}</td>
                    <td className="py-3 text-muted-foreground max-w-[220px] truncate">{f.original_filename}</td>
                    <td className="py-3">{fmtDate(f.due_date)}</td>
                    <td className="py-3 text-right font-medium">{f.amount != null ? fmtEuro(f.amount) : "—"}</td>
                    <td className="py-3 text-right"><StatusBadge status={f.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
